import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TFetchParams } from '@/types/fetch.type';
import { filterEmptyValues } from '@/utils/object';
import { TSpace } from '../types/space.type';

export type TGetListSpacesResponse = ApiResponse<TSpace[]>;
export type TGetListSpacesParams = TFetchParams;

export const getListSpaces = async (params: TGetListSpacesParams): Promise<TGetListSpacesResponse> => {
  const url = '/space';

  const filteredParams = filterEmptyValues(params);

  return axiosInstance.get(url, { params: filteredParams });
};

export const GET_LIST_SPACES_QUERY_KEY = 'get-list-space';

export const getListSpacesQueryOptions = <TData = TSpace[]>(
  params: TGetListSpacesParams,
  select?: (data: TGetListSpacesResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_SPACES_QUERY_KEY,
      {
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListSpaces(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListSpacesQuery<TData>(options: {
  params: TGetListSpacesParams;
  queryConfig?: QueryConfig<typeof getListSpacesQueryOptions<TData>>;
  select: (data: TGetListSpacesResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListSpacesQuery(options: {
  params: TGetListSpacesParams;
  queryConfig?: QueryConfig<typeof getListSpacesQueryOptions<TGetListSpacesResponse>>;
}): ReturnType<typeof useQuery<TGetListSpacesResponse>>;

// Implementation
export function useListSpacesQuery<TData = TGetListSpacesResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListSpacesParams;
  queryConfig?: QueryConfig<typeof getListSpacesQueryOptions<TData | TGetListSpacesResponse>>;
  select?: (data: TGetListSpacesResponse) => TData;
}) {
  return useQuery({
    ...getListSpacesQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListSpacesResponse ? TGetListSpacesResponse : TData>>;
}
